/**
 * SMS Automation Contacts Page
 * Route: /marketing/automations/sms/:id/contacts
 *
 * Mirrors EmailAutomationContactsPage: full-height DataTable that scrolls
 * inside the page, back arrow + contact count in the header, Automation Stats
 * link, and a FilterSelect for status. The per-contact modal follows the SMS
 * campaign one (SmsCampaignSentContactsPage): patient link, per-step delivery
 * history, error details, and the Twilio event log.
 *
 * Two filters answer two different questions and are easy to confuse:
 *   - "Filter by status" is the CONTACT's status — where it stands in the
 *     sequence now (active / completed / unsubscribed / invalid / failed).
 *   - "Step" + "Outcome" are about SEND rows — the attempts behind one step's
 *     numbers on the stats page. A step's failed count is attempts, not
 *     contacts: a transient failure is retried up to three times and each
 *     attempt is its own row, so it routinely runs ahead of the contact count.
 * Both are kept in the URL (?stepId=&sendStatus=) so the stats page can link
 * straight to the contacts behind a number. Its other boxes link with ?status=
 * and ?lead=, which seed those two filters.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Loader2, Search } from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { FilterSelect } from '@/components/ui/FilterSelect';
import type { FilterSelectOption } from '@/components/ui/FilterSelect';
import * as marketingSvc from '@/services/marketing.service';
import type { SmsAutomationContactRow, SmsAutomationSend, SmsAutomationContactEvent, SmsAutomationContactDetail, SmsAutomationUpcomingStep, ContactLead } from '@/services/marketing.service';
import { ContactLeadCell, ContactLeadFilter, ContactLeadList, ContactLeadOpener, leadFilterFromUrl, leadParam, placeLeadsOnSends, useContactLeadModal } from '@/components/marketing/ContactLeads';
import { formatPhone } from '@/lib/utils';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { pacificToWallClock, getTimezoneAbbreviation } from '@/lib/facilityTimezone';

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Send/event timestamps arrive as Pacific wall-clock DATETIME strings
// (config('app.timezone')); reproject into the viewer's zone and suffix the
// abbreviation so attempted/delivered times are unambiguous.
function fmtDateTime(dateStr: string | null | undefined, tz: string): string {
  if (!dateStr) return '—';
  const m = dateStr.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return '—';
  const wall = pacificToWallClock(m[1], m[2], tz);
  const [y, mo, d] = wall.date.split('-').map(Number);
  const [h, mi] = wall.time.split(':').map(Number);
  const dateObj = new Date(y, mo - 1, d, h, mi);
  if (isNaN(dateObj.getTime())) return '—';
  const datePart = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} ${timePart} ${getTimezoneAbbreviation(tz, dateObj)}`;
}

// Step 1's delay is measured from enrollment; every later step's is measured
// from the previous send (see ProcessSmsAutomationSteps).
function formatDelayText(minutes: number, stepOrder: number): string {
  const anchor = stepOrder <= 1 ? 'after being added' : 'after the previous step';
  if (minutes === 0) return `immediately ${anchor}`;
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ${anchor}`;
  if (minutes < 1440) {
    const h = Math.round((minutes / 60) * 10) / 10;
    return `${h} hour${h !== 1 ? 's' : ''} ${anchor}`;
  }
  const d = Math.round((minutes / 1440) * 10) / 10;
  return `${d} day${d !== 1 ? 's' : ''} ${anchor}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

type ContactOutcome = {
  tone: 'good' | 'warn' | 'stop' | 'info';
  title: string;
  whenLabel: string;
  when: string | null;
  lines: string[];
};

/**
 * Turns the status badge into an explanation.
 *
 * Nothing here is ever hard-deleted: 'removed' and every other terminal status
 * is a flag on the enrollment row, which stays for history and simply stops
 * receiving steps. Only the nightly contact sync writes 'removed', and only
 * when the automation's exit criteria is "contacts are removed from the list"
 * (see SyncAutomationContacts::syncSmsAutomationContacts), so the reason is
 * always the same one: the number stopped matching the entry list.
 */
function describeOutcome(o: {
  status: string;
  currentStepOrder: number;
  /** When the contact left 'active': removedDate, or updateDate for the writers that set neither. */
  stoppedAt: string | null;
  completedDate: string | null;
  totalSteps: number;
  exitCriteria: number;
  allowReentry: boolean;
  entryListName: string | null;
  optedOut: boolean;
}): ContactOutcome | null {
  const remaining = Math.max(0, o.totalSteps - o.currentStepOrder);
  const plural = o.totalSteps === 1 ? '' : 's';
  const stepsLine = remaining > 0
    ? `They stopped after step ${o.currentStepOrder} of ${o.totalSteps} — the remaining ${remaining} step${remaining === 1 ? '' : 's'} will never send.`
    : `They had already received every step, so nothing was cut short.`;
  const reentryLine = o.allowReentry
    ? 'Re-entry is on: if this number matches the entry list again, the next sync re-adds it as a new enrollment starting back at step 1.'
    : 'Re-entry is off: this number will not be re-added even if it matches the entry list again.';
  const listName = o.entryListName ? `"${o.entryListName}"` : 'the entry list';

  switch (o.status) {
    case 'removed':
      return {
        tone: 'warn',
        title: 'Removed from the entry list',
        whenLabel: 'Removed',
        when: o.stoppedAt,
        lines: [
          'Removed means flagged, not deleted — the enrollment is kept for history and just stops receiving steps.',
          o.exitCriteria === 1
            ? `The nightly contact sync found this number no longer matches ${listName}, and this automation is set to exit contacts when they are removed from the list.`
            : `This number stopped matching ${listName}. The automation is now set to keep contacts enrolled until they have received every message, so new removals like this one no longer happen.`,
          stepsLine,
          reentryLine,
        ],
      };
    case 'completed':
      return {
        tone: 'good',
        title: 'Finished the automation',
        whenLabel: 'Completed',
        when: o.completedDate,
        lines: [
          `This contact received all ${o.totalSteps} step${plural} and left the sequence normally.`,
          'Completed contacts are never restarted by the sync — re-entry only re-admits contacts that were removed from the list.',
        ],
      };
    case 'unsubscribed':
      return {
        tone: 'stop',
        title: 'Opted out of SMS',
        whenLabel: 'Stopped',
        when: o.stoppedAt,
        lines: [
          'This number is in the account opt-out ledger — it replied STOP to a message, or was opted out on the Twilio side.',
          'Opt-outs are account-wide: no automation or campaign will text this number again until the opt-out is lifted.',
          stepsLine,
          'The sync will not re-add this number while the opt-out stands, whatever the re-entry setting says.',
        ],
      };
    case 'invalid':
      return {
        tone: 'stop',
        title: 'Unusable phone number',
        whenLabel: 'Flagged',
        when: o.stoppedAt,
        lines: [
          'This number is on the account invalid-contact list, so it is never texted again and never re-added by the sync.',
          stepsLine,
        ],
      };
    case 'failed':
      return {
        tone: 'stop',
        title: 'Sending failed',
        whenLabel: 'Failed',
        when: o.stoppedAt,
        lines: [
          'Delivery failed for this contact and the sequence was stopped. The carrier reason is on the failed send below.',
          stepsLine,
        ],
      };
    case 'active':
      if (o.optedOut) {
        return {
          tone: 'warn',
          title: 'Active, but opted out',
          whenLabel: '',
          when: null,
          lines: [
            'This enrollment still reads Active while the number sits in the account opt-out ledger — older opt-outs never updated enrollment rows.',
            'Nothing will send to it: the step processor skips opted-out numbers and flips this contact to Unsubscribed the next time it reaches them.',
          ],
        };
      }
      return null;
    default:
      return null;
  }
}

const STATUS_ICON: Record<string, { bg: string; color: string }> = {
  queued:      { bg: '#fff3cd', color: '#856404' },
  sending:     { bg: '#fff3cd', color: '#856404' },
  sent:        { bg: '#cce5ff', color: '#004085' },
  delivered:   { bg: '#d4edda', color: '#155724' },
  undelivered: { bg: '#ffe5d0', color: '#8a4b08' },
  failed:      { bg: '#f8d7da', color: '#721c24' },
};

const statusColors: Record<string, string> = {
  active: '#155724',
  completed: '#004085',
  removed: '#856404',
  unsubscribed: '#721c24',
  invalid: '#b02a37',
  failed: '#5c636a',
};
const statusBg: Record<string, string> = {
  active: '#d4edda',
  completed: '#cce5ff',
  removed: '#fff3cd',
  unsubscribed: '#f8d7da',
  invalid: '#f8d0d4',
  failed: '#e2e3e5',
};
// 'invalid' = permanent bad/unreachable number (never retried);
// 'failed'  = never went through after repeated transient errors. Spelled out
// rather than left as a bare "Failed", which read as "this contact had a failed
// message" — a far more common thing, and counted somewhere else entirely.
const statusLabels: Record<string, string> = {
  invalid: 'Invalid number',
  failed: 'Stopped after retries',
};

export default function SmsAutomationContactsPage() {
  const { id } = useParams<{ id: string }>();
  const automationId = Number(id);
  const userTz = useUserTimezone();

  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [stepFilter, setStepFilter] = useState(() => searchParams.get('stepId') ?? '');
  const [sendStatusFilter, setSendStatusFilter] = useState(() => searchParams.get('sendStatus') ?? '');
  const [leadFilter, setLeadFilter] = useState(() => leadFilterFromUrl(searchParams.get('lead')));
  // Carried in from a stats-page drill-down so this list covers the same window
  // the clicked number was computed over. There is no date control on this page:
  // the range arrives with the link and goes when the send filters are cleared.
  const [sendDateFrom, setSendDateFrom] = useState(() => searchParams.get('date_from') ?? '');
  const [sendDateTo, setSendDateTo] = useState(() => searchParams.get('date_to') ?? '');
  const [selectedContact, setSelectedContact] = useState<SmsAutomationContactRow | null>(null);
  const perPage = 25;

  // State and URL move together, so a filtered view survives a refresh and can
  // be linked to from the step cards on the stats page.
  const applySendFilters = useCallback((nextStepId: string, nextSendStatus: string) => {
    setStepFilter(nextStepId);
    setSendStatusFilter(nextSendStatus);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (nextStepId) next.set('stepId', nextStepId); else next.delete('stepId');
    if (nextSendStatus) next.set('sendStatus', nextSendStatus); else next.delete('sendStatus');
    // The window only means anything alongside a send filter, so clearing both
    // clears it too rather than leaving an invisible date narrowing the list.
    if (!nextStepId && !nextSendStatus) {
      next.delete('date_from');
      next.delete('date_to');
      setSendDateFrom('');
      setSendDateTo('');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: autoData } = useQuery({
    queryKey: ['sms-automation-stats', automationId],
    queryFn: () => marketingSvc.getSmsAutomationStats(automationId),
    enabled: automationId > 0,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['sms-automation-contacts', automationId, page, search, statusFilter, leadFilter, stepFilter, sendStatusFilter, sendDateFrom, sendDateTo],
    queryFn: () => marketingSvc.listSmsAutomationContacts(automationId, {
      page,
      perPage,
      search,
      status: statusFilter || undefined,
      lead: leadParam(leadFilter),
      stepId: stepFilter ? Number(stepFilter) : undefined,
      sendStatus: sendStatusFilter || undefined,
      date_from: sendDateFrom || undefined,
      date_to: sendDateTo || undefined,
    }),
    enabled: automationId > 0,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['sms-automation-contact-stats', automationId, selectedContact?.contactId],
    queryFn: () => marketingSvc.getSmsAutomationContactStats(automationId, selectedContact!.contactId),
    enabled: selectedContact != null,
  });

  useEffect(() => {
    document.body.style.overflow = selectedContact != null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedContact]);

  const contacts = data?.contacts ?? [];
  const total = data?.total ?? 0;
  const leadCount = data?.leadCount;
  const contactCounts = autoData?.contactCounts ?? { total: 0, active: 0, completed: 0, removed: 0, unsubscribed: 0, invalid: 0, failed: 0 };
  const totalSteps = autoData?.stepCount ?? 0;

  const FILTER_OPTIONS = useMemo<FilterSelectOption[]>(() => [
    { label: 'All Contacts', value: '', count: contactCounts.total, color: '#3b82f6', textColor: '#1d4ed8' },
    { label: 'Active', value: 'active', count: contactCounts.active, color: '#22c55e', textColor: '#15803d' },
    { label: 'Completed', value: 'completed', count: contactCounts.completed, color: '#0ea5e9', textColor: '#0369a1' },
    { label: 'Removed', value: 'removed', count: contactCounts.removed, color: '#f59e0b', textColor: '#b45309' },
    { label: 'Unsubscribed', value: 'unsubscribed', count: contactCounts.unsubscribed, color: '#f97316', textColor: '#c2410c' },
    { label: 'Invalid number', value: 'invalid', count: contactCounts.invalid, color: '#dc2626', textColor: '#991b1b' },
    { label: 'Stopped after retries', value: 'failed', count: contactCounts.failed, color: '#94a3b8', textColor: '#475569' },
  ], [contactCounts]);

  // Steps to filter by, keyed on stepId. A step added after the automation was
  // already running has no send history of its own and correctly reads 0 here —
  // its number on the stats page is 0 for the same reason.
  // Removed steps come after the live ones: their sends still exist, and the
  // stats page's Removed steps cards link here by their stepId.
  const stepStats = useMemo(
    () => [...(autoData?.stepStats ?? []), ...(autoData?.removedStepStats ?? [])],
    [autoData],
  );
  const STEP_OPTIONS = useMemo<FilterSelectOption[]>(() => [
    { label: 'All steps', value: '', color: '#3b82f6', textColor: '#1d4ed8' },
    ...stepStats.map((s: any) => ({
      label: s.previousStepOrder ? `Removed step (was ${s.previousStepOrder})` : `Step ${s.stepOrder}`,
      value: String(s.stepId),
      count: s.totalSends,
      color: s.previousStepOrder ? '#94a3b8' : '#8b5cf6',
      textColor: s.previousStepOrder ? '#475569' : '#6d28d9',
    })),
  ], [stepStats]);

  const selectedStep = useMemo(
    () => stepStats.find((s: any) => String(s.stepId) === stepFilter),
    [stepStats, stepFilter],
  );

  // Counts come from the selected step so they reconcile with its card on the
  // stats page, including the card's own definition of "Sent": that number counts
  // sent AND delivered, so the option matching it asks for both. 'Awaiting
  // receipt' is the literal 'sent' status on its own — the difference between the
  // two — which is why the card's Sent figure links here as 'sent,delivered'.
  const SEND_STATUS_OPTIONS = useMemo<FilterSelectOption[]>(() => [
    { label: 'Any outcome', value: '', count: selectedStep?.totalSends, color: '#3b82f6', textColor: '#1d4ed8' },
    { label: 'Sent or delivered', value: 'sent,delivered', count: selectedStep ? selectedStep.sent : undefined, color: '#2e7d32', textColor: '#15803d' },
    { label: 'Delivered', value: 'delivered', count: selectedStep ? selectedStep.delivered : undefined, color: '#a855f7', textColor: '#7e22ce' },
    { label: 'Awaiting receipt', value: 'sent', count: selectedStep ? Math.max(0, selectedStep.sent - selectedStep.delivered) : undefined, color: '#22c55e', textColor: '#15803d' },
    { label: 'Failed', value: 'failed', count: selectedStep ? selectedStep.failed : undefined, color: '#dc2626', textColor: '#991b1b' },
    { label: 'Undelivered', value: 'undelivered', count: selectedStep ? selectedStep.undelivered : undefined, color: '#f59e0b', textColor: '#b45309' },
  ], [selectedStep]);

  const columns: DataTableColumn<SmsAutomationContactRow>[] = useMemo(() => [
    {
      key: 'phoneNumber',
      header: 'Phone',
      sortable: true,
      render: (row) => <span>{formatPhone(row.phoneNumber)}</span>,
    },
    {
      key: 'firstName',
      header: 'Name',
      sortable: true,
      render: (row) => <span>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <>
          <span className="sac-status-badge" style={{ background: statusBg[row.status] ?? '#e9ecef', color: statusColors[row.status] ?? '#333' }}>
            {statusLabels[row.status] ?? capitalize(row.status)}
          </span>
          {/* Historical opt-outs never updated the enrollment row, so a number
              can be in the opt-out ledger while still 'active'. */}
          {row.optedOut && row.status !== 'unsubscribed' && (
            <span className="sac-optout-pill">Opted out</span>
          )}
        </>
      ),
    },
    {
      key: 'currentStepOrder',
      header: 'Step',
      sortable: true,
      render: (row) => {
        if (row.status === 'completed') {
          return <span style={{ color: '#28a745', fontWeight: 600 }}>Complete</span>;
        }
        return <span>{row.currentStepOrder} / {totalSteps}</span>;
      },
    },
    // Only while scoped to one step: what that step actually did for this
    // contact. Without it the filtered list says who is behind a number but not
    // why, which is the reason for opening it.
    ...(stepFilter ? [{
      key: 'stepSend',
      header: selectedStep
        ? (selectedStep.previousStepOrder ? `Removed step ${selectedStep.previousStepOrder} outcome` : `Step ${selectedStep.stepOrder} outcome`)
        : 'Step outcome',
      render: (row: SmsAutomationContactRow) => {
        const s = row.stepSend;
        if (!s || !s.deliveryStatus) return <span className="text-gray-400">—</span>;
        const si = STATUS_ICON[s.deliveryStatus] ?? { bg: '#e9ecef', color: '#333' };
        // The badge is the attempt that matched the filter. When the step later
        // ended differently — a failure whose retry got through — say so, rather
        // than showing only one half of what happened.
        const endedDifferently = s.latestDeliveryStatus != null && s.latestDeliveryStatus !== s.deliveryStatus;
        return (
          <div>
            <span className="sac-status-badge" style={{ background: si.bg, color: si.color }}>
              {s.deliveryStatus === 'sent' ? 'Awaiting receipt' : capitalize(s.deliveryStatus)}
            </span>
            {s.attempts > 1 && (
              <span className="sac-optout-pill" title="Each retry of a transient failure is its own send row">
                {s.attempts} attempts
              </span>
            )}
            {endedDifferently && (
              <div className="text-xs text-gray-500 mt-0.5">
                later {s.latestDeliveryStatus === 'sent' ? 'sent' : s.latestDeliveryStatus}
              </div>
            )}
            {s.errorMessage && (
              <div className="text-xs text-red-600 mt-0.5 max-w-xs truncate" title={s.errorMessage}>
                {s.errorMessage}
              </div>
            )}
          </div>
        );
      },
    } as DataTableColumn<SmsAutomationContactRow>] : []),
    {
      key: 'leads',
      header: 'Lead',
      render: (row) => <ContactLeadCell leads={row.leads} />,
    },
    {
      key: 'addedDate',
      header: 'Entry Date',
      sortable: true,
      render: (row) => fmtDate(row.addedDate),
    },
    {
      key: 'completedDate',
      header: 'Completed',
      sortable: true,
      render: (row) => fmtDate(row.completedDate),
    },
  ], [totalSteps, stepFilter, selectedStep]);

  const closeModal = useCallback(() => setSelectedContact(null), []);

  // The lead modal opens over the list; its Spam / Trash / Convert can change
  // what the Lead column and the open contact show, so both reload after it.
  const queryClient = useQueryClient();
  const leadModal = useContactLeadModal(useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sms-automation-contacts', automationId] });
    void queryClient.invalidateQueries({ queryKey: ['sms-automation-contact-stats', automationId] });
  }, [queryClient, automationId]));

  return (
    <ContactLeadOpener value={leadModal.openLead}>
      <style>{`
        .sac-status-badge {
          display: inline-block; padding: 3px 10px; border-radius: 12px;
          font-size: 12px; font-weight: 600;
        }
        .sac-optout-pill {
          display: inline-block; padding: 2px 8px; border-radius: 10px; margin-left: 6px;
          font-size: 11px; font-weight: 600; background: #ffe8d9; color: #c2410c;
          white-space: nowrap;
        }

        .sac-modal-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: center;
          padding: 40px 16px; overflow: hidden;
        }
        /* The shell is capped to the viewport and only .sac-modal-body scrolls,
           so the identity header stays put however long the detail runs. */
        .sac-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 640px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25);
          display: flex; flex-direction: column;
          max-height: 100%; min-height: 0;
        }
        .sac-modal-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 12px; padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
          flex-shrink: 0;
        }
        .sac-modal-close { background: none; border: none; font-size: 22px; cursor: pointer; color: #999; line-height: 1; padding: 0 4px; }
        .sac-modal-close:hover { color: #333; }
        .sac-modal-body { padding: 20px; overflow-y: auto; flex: 1; min-height: 0; }
        .sac-loading { text-align: center; padding: 40px; color: #999; }

        .sac-headline { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
        .sac-headline-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .sac-contact-name { font-size: 18px; font-weight: 600; color: #333; margin: 0; }
        .sac-contact-phone { color: #666; font-size: 14px; margin: 0; }
        .sac-name-link {
          font-size: 18px; font-weight: 600; color: #00aff0; text-decoration: none;
        }
        .sac-name-link:hover { text-decoration: underline; }

        .sac-section { margin-bottom: 20px; }
        .sac-section:last-child { margin-bottom: 0; }
        .sac-section-title {
          font-size: 13px; font-weight: 600; color: #333;
          text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e5e5;
        }
        .sac-info-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px;
        }
        .sac-info-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #999; }
        .sac-info-value { font-size: 14px; color: #333; margin-top: 2px; }

        .sac-send-card {
          background: #f8f9fa; border-radius: 6px; padding: 12px 14px; margin-bottom: 10px;
        }
        .sac-send-card:last-child { margin-bottom: 0; }
        .sac-send-card.error { background: #fff5f5; }
        .sac-send-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .sac-send-msg {
          font-size: 13px; color: #555; background: white; border: 1px solid #eee;
          border-radius: 5px; padding: 8px 10px; margin: 6px 0; white-space: pre-wrap;
        }
        .sac-send-meta { font-size: 12px; color: #888; }
        .sac-sid { font-size: 11px; color: #aaa; margin-top: 4px; font-family: monospace; word-break: break-all; }
        .sac-error-box {
          background: #fff5f5; border: 1px solid #f8d7da; border-radius: 6px;
          padding: 10px 12px; margin-top: 8px;
        }
        .sac-error-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #721c24; margin-bottom: 2px; }
        .sac-error-msg { font-size: 13px; color: #721c24; line-height: 1.5; word-break: break-word; }

        .sac-events-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .sac-events-table th {
          text-align: left; padding: 8px 12px; font-size: 11px;
          font-weight: 600; text-transform: uppercase; color: #666;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5;
        }
        .sac-events-table td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; color: #333; }
        .sac-events-table tr:last-child td { border-bottom: none; }
        .sac-event-badge {
          display: inline-block; padding: 2px 8px; border-radius: 10px;
          font-size: 11px; font-weight: 600; text-transform: capitalize;
        }
        .sac-empty { text-align: center; color: #999; padding: 20px; font-size: 13px; }

        .sac-section-toggle {
          display: flex; align-items: center; justify-content: space-between;
          width: 100%; background: none; border: none; cursor: pointer;
          font-size: 13px; font-weight: 600; color: #333;
          text-transform: uppercase; letter-spacing: 0.5px;
          padding: 0 0 6px; margin-bottom: 12px; border-bottom: 1px solid #e5e5e5;
        }
        .sac-section-toggle:hover { color: #00aff0; }
        .sac-section-toggle .sac-chevron { transition: transform 0.15s ease; color: #999; }
        .sac-section-toggle .sac-chevron.open { transform: rotate(180deg); }

        .sac-upcoming-card {
          background: #fff; border: 1px dashed #d8dee4; border-radius: 6px;
          padding: 12px 14px; margin-bottom: 10px;
        }
        .sac-upcoming-card.next { border-style: solid; border-color: #bcdcc4; background: #f6fbf7; }
        .sac-next-pill {
          display: inline-block; margin-left: 8px; padding: 2px 8px; border-radius: 10px;
          font-size: 11px; font-weight: 600; background: #d4edda; color: #155724;
        }
        .sac-schedule-time { font-size: 13px; font-weight: 600; color: #2e7d32; }
        .sac-schedule-note {
          font-size: 12px; color: #888; line-height: 1.5; margin-top: 8px;
        }
        .sac-schedule-note.warn {
          background: #fff8e6; border: 1px solid #f5e2b8; color: #8a6d24;
          border-radius: 6px; padding: 8px 10px; margin: 0 0 10px;
        }

        .sac-outcome {
          border: 1px solid #e5e5e5; border-left-width: 4px; border-radius: 6px;
          padding: 12px 14px; background: #fafbfc;
        }
        .sac-outcome.good { border-left-color: #28a745; background: #f6fbf7; }
        .sac-outcome.warn { border-left-color: #f0b429; background: #fffaf0; }
        .sac-outcome.stop { border-left-color: #dc3545; background: #fff5f5; }
        .sac-outcome.info { border-left-color: #0d6efd; background: #f5f9ff; }
        .sac-outcome-top {
          display: flex; justify-content: space-between; align-items: baseline;
          gap: 12px; margin-bottom: 8px;
        }
        .sac-outcome-title { font-size: 14px; font-weight: 600; color: #333; }
        .sac-outcome-when { font-size: 12px; color: #888; white-space: nowrap; }
        .sac-outcome-list { margin: 0; padding-left: 18px; }
        .sac-outcome-list li { font-size: 13px; color: #555; line-height: 1.55; margin-bottom: 4px; }
        .sac-outcome-list li:last-child { margin-bottom: 0; }
      `}</style>

      <div className="flex flex-col h-full overflow-hidden p-6">
        {/* Header */}
        <div className="flex-shrink-0 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to={`/marketing/automations/sms/${automationId}/stats`}
                className="p-1 rounded hover:bg-slate-100"
              >
                <ArrowLeft className="h-5 w-5 text-slate-500" />
              </Link>
              <h1 className="text-xl font-semibold text-slate-800">Automation Contacts</h1>
              <div className="flex items-center gap-2 ml-2">
                <span className="text-2xl font-bold text-green-600">{fmtNum(contactCounts.total)}</span>
                <span className="text-sm font-semibold text-slate-500">{contactCounts.total === 1 ? 'contact' : 'contacts'}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/marketing/automations/sms/${automationId}/stats`}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Automation Stats
              </Link>
            </div>
          </div>

          {/* Contact status, leads and step/outcome filters + search */}
          <div className="flex flex-wrap items-center gap-4">
            <FilterSelect
              options={FILTER_OPTIONS}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              label="Filter by status"
              className="w-60"
            />
            <ContactLeadFilter
              value={leadFilter}
              onChange={(v) => { setLeadFilter(v); setPage(1); }}
              total={contactCounts.total}
              leadCount={leadCount}
              recipientCount={data?.leadRecipientCount}
              referralCount={data?.leadReferralCount}
            />
            <FilterSelect
              options={STEP_OPTIONS}
              value={stepFilter}
              onChange={(v) => applySendFilters(v, sendStatusFilter)}
              label="Step"
              className="w-44"
            />
            <FilterSelect
              options={SEND_STATUS_OPTIONS}
              value={sendStatusFilter}
              onChange={(v) => applySendFilters(stepFilter, v)}
              label="Outcome"
              className="w-52"
            />
            <div className="ml-auto">
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by phone or name..."
                className="w-64"
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>

          {/* Contacts vs attempts is the single most confusing thing on these two
              screens, so the filtered view says outright which one it is showing. */}
          {(stepFilter || sendStatusFilter) && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <span>
                <strong>{fmtNum(total)}</strong> contact{total === 1 ? '' : 's'}
                {selectedStep
                  ? (selectedStep.previousStepOrder
                    ? ` with a send from removed step ${selectedStep.previousStepOrder}`
                    : ` with a step ${selectedStep.stepOrder} send`)
                  : ' with a send'}
                {sendStatusFilter ? ` marked ${sendStatusFilter.split(',').join(' or ')}` : ''}
                {sendDateFrom && sendDateTo ? ` between ${sendDateFrom} and ${sendDateTo}` : ''}
                {selectedStep && sendStatusFilter === 'failed' && selectedStep.failed !== total
                  ? ` — the step card counts ${fmtNum(selectedStep.failed)} failed attempts, retries included`
                  : ''}
                .
              </span>
              <button
                type="button"
                className="underline hover:text-gray-900"
                onClick={() => applySendFilters('', '')}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 mt-4">
          <DataTable<SmsAutomationContactRow>
            data={contacts}
            columns={columns}
            keyField="contactId"
            loading={isLoading}
            page={page}
            pageSize={perPage}
            total={total}
            onPageChange={setPage}
            onRowClick={(row) => setSelectedContact(row)}
            rowClassName={() => 'cursor-pointer hover:bg-slate-50'}
          />
        </div>
      </div>

      {/* Contact Detail Modal */}
      {selectedContact != null && !leadModal.isOpen && (
        <div className="sac-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="sac-modal">
            {/* Identity lives in the fixed header; the detail fetch wins once it
                lands, with the table row covering the loading gap. */}
            <ContactModalHeader
              contact={detail?.contact ?? selectedContact}
              onClose={closeModal}
            />
            <div className="sac-modal-body">
              {detailLoading ? (
                <div className="sac-loading"><Loader2 className="h-6 w-6 animate-spin inline-block" /> Loading...</div>
              ) : (
                <ContactDetailBody
                  row={selectedContact}
                  contact={detail?.contact}
                  sends={detail?.sends ?? []}
                  leads={detail?.leads ?? []}
                  events={detail?.events ?? []}
                  upcomingSteps={detail?.upcomingSteps ?? []}
                  automationStatus={detail?.automationStatus ?? autoData?.status ?? ''}
                  totalSteps={totalSteps}
                  exitCriteria={Number(autoData?.exitCriteria ?? 0)}
                  allowReentry={Boolean(autoData?.allowReentry)}
                  entryListName={autoData?.entryListName ?? null}
                  tz={userTz}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {leadModal.modal}
    </ContactLeadOpener>
  );
}

/**
 * Fixed modal header: who this is, at a glance. The patient name doubles as the
 * link to the chart — contacts with no patientID (list-only numbers) fall back
 * to plain text, and a nameless contact leads with the phone number instead.
 */
function ContactModalHeader({ contact, onClose }: {
  contact: Pick<SmsAutomationContactRow, 'firstName' | 'lastName' | 'phoneNumber' | 'status' | 'patientID' | 'optedOut'>;
  onClose: () => void;
}) {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  const phone = formatPhone(contact.phoneNumber);

  return (
    <div className="sac-modal-header">
      <div className="sac-headline">
        {name
          ? (contact.patientID
              ? <Link to={`/patients/${contact.patientID}`} className="sac-name-link">{name}</Link>
              : <p className="sac-contact-name">{name}</p>)
          : <p className="sac-contact-name">{phone}</p>}
        <div className="sac-headline-meta">
          {name && <span className="sac-contact-phone">{phone}</span>}
          <span
            className="sac-status-badge"
            style={{ background: statusBg[contact.status] ?? '#e9ecef', color: statusColors[contact.status] ?? '#333' }}
          >
            {statusLabels[contact.status] ?? capitalize(contact.status)}
          </span>
          {contact.optedOut && contact.status !== 'unsubscribed' && (
            <span className="sac-optout-pill">Opted out of SMS</span>
          )}
        </div>
      </div>
      <button type="button" className="sac-modal-close" onClick={onClose}>&times;</button>
    </div>
  );
}

function ContactDetailBody({ row, contact, sends, leads, events, upcomingSteps, automationStatus, totalSteps, exitCriteria, allowReentry, entryListName, tz }: {
  row: SmsAutomationContactRow;
  contact: SmsAutomationContactDetail['contact'] | undefined;
  sends: SmsAutomationSend[];
  leads: ContactLead[];
  events: SmsAutomationContactEvent[];
  upcomingSteps: SmsAutomationUpcomingStep[];
  automationStatus: string;
  totalSteps: number;
  exitCriteria: number;
  allowReentry: boolean;
  entryListName: string | null;
  tz: string;
}) {
  // The detail fetch is authoritative; the table row covers the gap while an
  // older API build (no `contact` in the payload) is still deployed.
  const c = contact ?? row;
  const optedOut = c.optedOut ?? row.optedOut;

  const outcome = describeOutcome({
    status: c.status,
    currentStepOrder: c.currentStepOrder,
    stoppedAt: contact?.removedDate ?? contact?.updatedAt ?? null,
    completedDate: c.completedDate,
    totalSteps,
    exitCriteria,
    allowReentry,
    entryListName,
    optedOut,
  });

  // The event log is the noisiest section and the least often needed, so it
  // starts collapsed with its count on the toggle.
  const [eventsOpen, setEventsOpen] = useState(false);

  // Newest first — the API returns them oldest-first for the step timeline, but
  // in a log the latest delivery update is what you came to read.
  const orderedEvents = useMemo(
    () => [...events].sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? '')),
    [events],
  );

  // sms_events carries no automation-send linkage, so the Twilio SID is the only
  // path back to the step that produced each event.
  const stepBySid = useMemo(() => {
    const map: Record<string, number> = {};
    sends.forEach((sd) => { if (sd.twilioMessageSid) map[sd.twilioMessageSid] = sd.stepOrder; });
    return map;
  }, [sends]);

  // Each request-info lead goes on the last send of the step it came from.
  const placedLeads = useMemo(
    () => placeLeadsOnSends(sends.map((sd) => ({ key: String(sd.sendId), stepId: sd.stepId })), leads),
    [sends, leads],
  );

  return (
    <>
      {/* Contact information */}
      <div className="sac-section">
        <div className="sac-section-title">Contact Information</div>
        <div className="sac-info-grid">
          <div>
            <div className="sac-info-label">Added</div>
            <div className="sac-info-value">{fmtDateTime(c.addedDate, tz)}</div>
          </div>
          <div>
            <div className="sac-info-label">Step Progress</div>
            <div className="sac-info-value">{c.currentStepOrder} / {totalSteps}</div>
          </div>
          <div>
            <div className="sac-info-label">Completed</div>
            <div className="sac-info-value">{fmtDateTime(c.completedDate, tz)}</div>
          </div>
          <div>
            {/* Terminal statuses carry their own date in Status Details below, so
                this slot answers the more useful question instead. */}
            <div className="sac-info-label">{c.status === 'active' ? 'Next Send' : 'Steps Remaining'}</div>
            <div className="sac-info-value">
              {c.status === 'active'
                ? fmtDateTime(c.nextSendAt, tz)
                : Math.max(0, totalSteps - c.currentStepOrder)}
            </div>
          </div>
        </div>
      </div>

      {/* Why this contact is in the state it is in */}
      {outcome && (
        <div className="sac-section">
          <div className="sac-section-title">Status Details</div>
          <div className={`sac-outcome ${outcome.tone}`}>
            <div className="sac-outcome-top">
              <span className="sac-outcome-title">{outcome.title}</span>
              {outcome.when && (
                <span className="sac-outcome-when">{outcome.whenLabel} {fmtDateTime(outcome.when, tz)}</span>
              )}
            </div>
            <ul className="sac-outcome-list">
              {outcome.lines.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Message history */}
      <div className="sac-section">
        <div className="sac-section-title">Message History</div>
        {sends.length === 0 ? (
          <div className="sac-empty">
            {optedOut
              ? 'No messages sent — this contact opted out of SMS marketing before the first step went out.'
              : c.status === 'active' && c.nextSendAt
                ? 'Nothing sent yet — this contact is still waiting on their first step. See Scheduled below.'
                : 'No sends recorded for this contact.'}
          </div>
        ) : (
          sends.map((send) => {
            const isFailed = send.deliveryStatus === 'failed' || send.deliveryStatus === 'undelivered';
            const si = STATUS_ICON[send.deliveryStatus] ?? { bg: '#e9ecef', color: '#333' };
            return (
              <div key={send.sendId} className={`sac-send-card${isFailed ? ' error' : ''}`}>
                <div className="sac-send-top">
                  <strong style={{ fontSize: 14 }}>Step {send.stepOrder}</strong>
                  <span className="sac-status-badge" style={{ background: si.bg, color: si.color }}>
                    {send.deliveryStatus}
                  </span>
                </div>
                {send.messageBody && <div className="sac-send-msg">{send.messageBody}</div>}
                <div className="sac-send-meta">
                  Attempted: {fmtDateTime(send.attemptedAt ?? send.sentAt, tz)}
                  {send.deliveryStatus === 'delivered' && send.statusUpdatedAt && (
                    <> &nbsp;·&nbsp; Delivered: {fmtDateTime(send.statusUpdatedAt, tz)}</>
                  )}
                </div>
                {send.twilioMessageSid && <div className="sac-sid">SID: {send.twilioMessageSid}</div>}
                {send.errorMessage && (
                  <div className="sac-error-box">
                    <div className="sac-error-label">Error</div>
                    <div className="sac-error-msg">{send.errorMessage}</div>
                  </div>
                )}
                <ContactLeadList leads={placedLeads.byKey.get(String(send.sendId)) ?? []} tz={tz} className="mt-2" />
              </div>
            );
          })
        )}
        <ContactLeadList leads={placedLeads.unplaced} tz={tz} showStep className="mt-2.5" />
      </div>

      {/* Upcoming steps */}
      {upcomingSteps.length > 0 && (
        <div className="sac-section">
          <div className="sac-section-title">
            Scheduled ({upcomingSteps.length} step{upcomingSteps.length === 1 ? '' : 's'} remaining)
          </div>

          {automationStatus === 'paused' && (
            <div className="sac-schedule-note warn">
              This automation is paused, so nothing will send until it is resumed. These times do not
              shift while paused — anything already past due goes out shortly after you resume.
            </div>
          )}

          {upcomingSteps.map((step) => (
            <div key={step.stepId} className={`sac-upcoming-card${step.isNext ? ' next' : ''}`}>
              <div className="sac-send-top">
                <strong style={{ fontSize: 14 }}>
                  Step {step.stepOrder}
                  {step.isNext && <span className="sac-next-pill">Next</span>}
                </strong>
                <span className="sac-schedule-time">{fmtDateTime(step.scheduledAt, tz)}</span>
              </div>
              {step.messagePreview && <div className="sac-send-msg">{step.messagePreview}</div>}
              <div className="sac-send-meta">{capitalize(formatDelayText(step.delayMinutes, step.stepOrder))}</div>
            </div>
          ))}

          <div className="sac-schedule-note">
            Projected from this contact's next send time. The automation processor runs every 15
            minutes, so each message goes out on the first run at or after the time shown.
          </div>
        </div>
      )}

      {/* Event log */}
      {sends.length > 0 && (
        <div className="sac-section">
          <button
            type="button"
            className="sac-section-toggle"
            onClick={() => setEventsOpen((open) => !open)}
            aria-expanded={eventsOpen}
          >
            <span>Event Log{events.length > 0 ? ` (${events.length})` : ''}</span>
            <ChevronDown className={`sac-chevron h-4 w-4${eventsOpen ? ' open' : ''}`} />
          </button>
          {!eventsOpen ? null : orderedEvents.length > 0 ? (
            <table className="sac-events-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Event</th>
                  <th>Date</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {orderedEvents.map((ev, i) => {
                  const si = STATUS_ICON[ev.eventType] ?? STATUS_ICON.queued;
                  const evStep = ev.twilioMessageSid ? stepBySid[ev.twilioMessageSid] : undefined;
                  return (
                    <tr key={i}>
                      <td style={{ fontSize: 13, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>
                        {evStep != null ? `Step ${evStep}` : '—'}
                      </td>
                      <td>
                        <span className="sac-event-badge" style={{ background: si.bg, color: si.color }}>
                          {ev.eventType}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#888' }}>{fmtDateTime(ev.eventDate, tz)}</td>
                      <td style={{ fontSize: 12, color: '#999' }}>{ev.errorMessage || ev.errorCode || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="sac-empty">
              No events recorded yet. Events appear here once the Twilio status callback
              webhook receives delivery updates for this contact's messages.
            </div>
          )}
        </div>
      )}
    </>
  );
}
